import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './app/routeTree.gen';
import './index.css';

// FOR DEVELOPMENT ONLY to bypass authentication via jwt: 
// Pre-initialize all services to prevent circular dependency issues 
// import '@/app/services/serviceInitializer';

import { AuthContext, AuthProvider, useAuth } from './app/stores/auth';
import { AclProvider } from './app/stores/aclProvider';
import PageNotFound from './shared/components/error/PageNotFound';

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  
  interface RouterContext {
    auth: AuthContext;
  }
}

// Create a new router instance
const router = createRouter({ 
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  context: {
    auth: {
      isAuthenticated: false,
      token: null,
    }
  },
  defaultNotFoundComponent: PageNotFound 
});

function InnerApp() {
  const auth = useAuth()
  return <RouterProvider 
          router={router} 
          context={{ 
            auth: {
              isAuthenticated: auth.isAuthenticated,
              token: auth.decodedToken,
            },
           }} />
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      refetchOnMount: false, // Don't refetch on mount if data exists
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnReconnect: false, // Don't refetch on reconnect
      retry: 1, // Only retry once on failure
    },
  },
});

function App() {
  return (
    <AuthProvider>
      <AclProvider>
        <InnerApp />
      </AclProvider>
    </AuthProvider>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);